import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CertificateProductFormFields,
} from "@/pages/platform/certificate-product-form-fields";
import { certificateProductFormFromRow } from "@/pages/platform/certificate-product-form-defaults";
import type { CertificateProductFormValues } from "@/pages/platform/certificate-product-form-types";
import { parseCertificateProductFormToUpsert } from "@/pages/platform/certificate-product-form-submit";
import type { CertificateProductPriceRow } from "@/services/platformCertificatePricingService";
import {
  getCertificateDocumentTypeOptions,
  getCertificateProductPrice,
  upsertCertificateProductPrice,
} from "@/services/platformCertificatePricingService";
import { formatCertificatePricingProduct } from "@/utils/certificatePricingDisplay";

const QUERY_KEY = ["platform", "products"] as const;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuidParam(id: string | undefined): boolean {
  return Boolean(id && UUID_RE.test(id));
}

export function CertificateProductEditPage(): JSX.Element {
  const { productId } = useParams<{ productId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const idValid = isUuidParam(productId);

  const [form, setForm] = useState<CertificateProductFormValues | null>(null);

  const detailQuery = useQuery({
    queryKey: [...QUERY_KEY, "detail", productId] as const,
    queryFn: () => getCertificateProductPrice(productId!),
    enabled: idValid,
  });

  const docOptionsQuery = useQuery({
    queryKey: [...QUERY_KEY, "document-type-options"] as const,
    queryFn: getCertificateDocumentTypeOptions,
    staleTime: 300_000,
  });

  const editingRow: CertificateProductPriceRow | null = detailQuery.data ?? null;

  useEffect(() => {
    if (detailQuery.data) {
      setForm(certificateProductFormFromRow(detailQuery.data));
    }
  }, [detailQuery.data]);

  const saveMutation = useMutation({
    mutationFn: upsertCertificateProductPrice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, "detail", productId] });
      toast.success(t("platform.products.saveSuccess"));
      navigate("/products");
    },
    onError: () => {
      toast.error(t("platform.products.saveError"));
    },
  });

  const toggleRequiredDoc = useCallback((slug: string, checked: boolean): void => {
    setForm((f) =>
      f
        ? {
            ...f,
            requiredDocs: checked
              ? f.requiredDocs.includes(slug)
                ? f.requiredDocs
                : [...f.requiredDocs, slug]
              : f.requiredDocs.filter((s) => s !== slug),
          }
        : f,
    );
  }, []);

  const toggleFulfillmentMode = useCallback((slug: string, checked: boolean): void => {
    setForm((f) =>
      f
        ? {
            ...f,
            fulfillmentModes: checked
              ? f.fulfillmentModes.includes(slug)
                ? f.fulfillmentModes
                : [...f.fulfillmentModes, slug]
              : f.fulfillmentModes.filter((s) => s !== slug),
          }
        : f,
    );
  }, []);

  const noopAddTargetType = useCallback((): void => {
    /* edit mode: slug not editable */
  }, []);

  const handleSubmit = useCallback((): void => {
    if (form == null || editingRow == null) return;
    const parsed = parseCertificateProductFormToUpsert(form, "edit", editingRow);
    if (!parsed.ok) {
      toast.error(t(parsed.errorKey));
      return;
    }
    void saveMutation.mutate(parsed.body);
  }, [form, editingRow, saveMutation, t]);

  const isSaving = saveMutation.isPending;

  if (!idValid) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("platform.products.editPageTitle")} onBack={() => navigate("/products")} />
        <Card className="mt-4 shadow-sm">
          <CardContent className="flex min-h-[40vh] items-center justify-center py-12">
            <EmptyState
              title={t("platform.products.detailNotFoundTitle")}
              message={t("platform.products.detailNotFoundMessage")}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return <LoadingPage />;
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("platform.products.editPageTitle")} onBack={() => navigate("/products")} />
        <Card className="mt-4 shadow-sm">
          <CardContent className="flex min-h-[40vh] items-center justify-center py-12">
            <EmptyState
              title={t("platform.products.detailNotFoundTitle")}
              message={t("platform.products.detailNotFoundMessage")}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (form == null || editingRow == null) {
    return <LoadingPage />;
  }

  const productLabel = formatCertificatePricingProduct(editingRow.targetType, t);

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platform.products.editPageTitle")}
        description={
          <span className="font-medium text-foreground">{productLabel}</span>
        }
        onBack={() => navigate("/products")}
      />

      <div className="mt-4 space-y-4">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <p className="mb-4 text-sm text-muted-foreground">
              {t("platform.products.editDialogDescription")}
            </p>
            <CertificateProductFormFields
              mode="edit"
              suggestedTargetTypeSlugs={[]}
              documentOptionsLoading={docOptionsQuery.isLoading}
              documentOptionSlugs={docOptionsQuery.data ?? []}
              isSaving={isSaving}
              form={form}
              setForm={setForm as Dispatch<SetStateAction<CertificateProductFormValues>>}
              onAddTargetTypeChange={noopAddTargetType}
              toggleRequiredDoc={toggleRequiredDoc}
              toggleFulfillmentMode={toggleFulfillmentMode}
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="flex flex-row flex-wrap items-center justify-between gap-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/products")}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {t("pages.common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("platform.products.saving")}
                </>
              ) : (
                t("platform.products.save")
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
